import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import moment from 'moment';
import MaskedInput from 'react-text-mask';
import 'react-day-picker/lib/style.css';
import formatDate from 'react-day-picker/moment';
import ChevronRightIcon from '../SVG-components/chevron_right';
import styles from './CalendarInput.module.scss';

export const CALENDAR_INPUT_FORMAT = 'MM-DD-YYYY';
const fromYear = 1991;
const toYear = moment().year() + 10;
const fromMonth = moment([fromYear, 0]).toDate();
const toMonth = moment([toYear, 11]).endOf('month').toDate();

export const dateMask = [
    /[0-1]/, /\d/, '-', // MM
    /[0-3]/, /\d/, '-', // DD
    /\d/, /\d/, /\d/, /\d/ // YYYY
];

function isValidDate(value: string) {
    return moment(value, CALENDAR_INPUT_FORMAT, true).isValid();
}

const CalendarInput = ({
    value,
    onChange,
    inputClassName,
    hideLabel = false
}: {
    value: string;
    onChange: (formattedDate: string) => void;
    inputClassName?: string;
    hideLabel?: boolean;
}) => {
    const inputRef = useRef<any>(null);

    const [selectedDay, setSelectedDay] = useState<Date | undefined>(
        isValidDate(value) ? moment(value, CALENDAR_INPUT_FORMAT).toDate() : undefined
    );
    const [month, setMonth] = useState<Date>(new Date());

    const updateDate = (dateInput: Date | string, hidePicker: boolean) => {
        const date = moment(dateInput, CALENDAR_INPUT_FORMAT, true);
        if (!date.isValid()) return;

        const formatted = date.format(CALENDAR_INPUT_FORMAT);

        setSelectedDay(date.toDate());
        setMonth(date.toDate());
        onChange(formatted);

        if (hidePicker) inputRef.current?.hideDayPicker();
    };



    return (
        <div className={styles.calendarInput}>
            {!hideLabel && <label className={styles.label}>Select date</label>}

            <DayPickerInput
                ref={inputRef}
                value={value}
                onDayChange={(day: Date) => updateDate(day, true)}
                format={CALENDAR_INPUT_FORMAT}
                formatDate={formatDate.formatDate}
                parseDate={formatDate.parseDate}
                placeholder="MM-DD-YYYY"
                keepFocus={true}
                component={(props) => (
                    <MaskedInput
                        {...props}
                        className={inputClassName || styles.calendarInputField}
                        mask={dateMask}
                        guide={false}
                        value={value}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val.trim() === '') {
                                return;
                            }

                            if (isValidDate(val)) {
                                updateDate(val, true);
                            }
                        }}
                    />
                )}

                dayPickerProps={{
                    month,
                    fromMonth,
                    toMonth,
                    selectedDays: selectedDay,
                    onMonthChange: setMonth,
                    todayButton: 'Today',
                    captionElement: () => null,
                    navbarElement: (props) => (
                        <CustomNavBar {...props} handleMonthChange={(m) => {
                            updateDate(moment(m).startOf('month').toDate(), false);
                        }} />
                    )
                }}
            />
        </div>
    );
};

export default CalendarInput;


function YearMonthForm({ date, localeUtils, onChange }) {
    const months = localeUtils.getMonths();
    const years = Array.from({ length: toYear - fromYear + 1 }, (_, i) => fromYear + i);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const form = e.target.form!;
        const month = parseInt(form.month.value, 10);
        const year = parseInt(form.year.value, 10);

        onChange(moment({ year, month }).toDate());
    };

    return (
        <form className={styles.yearMonthForm}>
            <select name="month" onChange={handleChange} value={date.getMonth()}>
                {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>

            <select name="year" onChange={handleChange} value={date.getFullYear()}>
                {years.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
        </form>
    );
}

export function CustomNavBar({
    className,
    previousMonth,
    month,
    localeUtils,
    nextMonth,
    onPreviousClick,
    onNextClick,
    handleMonthChange
}) {
    return (
        <div className={classNames(styles.customNavBar, className)}>
            <button onClick={() => onPreviousClick()} disabled={!previousMonth}>
                <ChevronRightIcon className="day-picker-navbar__chevron day-picker-navbar__chevron--turn-left" />
            </button>

            <YearMonthForm date={month} localeUtils={localeUtils} onChange={handleMonthChange} />

            <button onClick={() => onNextClick()} disabled={!nextMonth}>
                <ChevronRightIcon className="day-picker-navbar__chevron" />
            </button>
        </div>
    );
}